import type { TFunction } from "i18next";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { cx } from "../../components/ui";
import { formatRequestTokens } from "../../lib/inference";
import { formatUsageCost } from "../../lib/money";
import type { InferenceTotals } from "../../lib/types";

/**
 * Four figures as one instrument, not four cards.
 *
 * A row of separate cards spends its border budget drawing boxes and leaves the
 * numbers competing with them for attention. One band divided by hairlines reads
 * as a single readout, which is what it is — the four are the same measurement of
 * the same traffic, and they are meant to be compared across, not each on their
 * own.
 *
 * Input and output tokens stay separate rather than summing to one figure, and
 * that is the point of having four cells: output is usually several times dearer
 * per token, so a combined total hides the half that actually moves the bill.
 *
 * **Each cell now carries the figure underneath it that explains it**, which is
 * the whole of this component's second job. Four headline numbers can say a
 * customer spent $40 on 4M input tokens and cannot say that 3.5M of those tokens
 * were cache hits, or that the 200k output tokens were most of the $40. Both are
 * the answer to "why is this bill what it is", and both were being thrown away.
 *
 * The sub-figures stay sub-figures deliberately. A second row of same-size
 * numbers would double what has to be read to find the one that matters, and the
 * headline is still the one somebody came for.
 *
 * `stale` dims the whole strip. The query keeps the previous window on screen
 * while a new range loads, which is the right call for a chart and the wrong one
 * for money — a figure from the last range that looks live is worse than a gap.
 */
export function StatStrip({
  totals,
  currency,
  caption,
  stale = false,
}: {
  totals: InferenceTotals;
  currency: string;
  /** What window these cover, e.g. "This month" or "Last 30 days". */
  caption: string;
  stale?: boolean;
}) {
  const { t } = useTranslation("ai");
  /*
   * What the prompt cache saved, which is the one figure here that is *not* part
   * of what was spent.
   *
   * It belongs under Spent anyway, because that is the number it modifies: the
   * reading is "you paid this, and this much less than you would have". Putting
   * it under Input, where the cached token counts live, would invite adding it to
   * a cost — and it is the cost that was avoided, not one that was incurred.
   */
  const saved = totals.cachedSavingMicros;

  const cells = [
    {
      label: t("stats.spent"),
      value: formatUsageCost(totals.costMicros, currency),
      lead: true,
      saved,
      /*
       * No breakdown under the headline, on either tab.
       *
       * This carried the spend split by direction — `$2.80 in · $8.20 out ·
       * $1.48 per-request` — and both surfaces have now said they do not want
       * it. Overview answers "how much am I spending", where three more currency
       * figures under the one number somebody came for is arithmetic they did
       * not ask to check. Activity lists every request beneath this strip with
       * its own cost, so the strip repeating a rolled-up version of the same
       * thing is a second answer to a question already answered better.
       *
       * Worth knowing if it is ever wanted back: the figures still arrive on
       * `totals` and are still stored per request, so this is a rendering
       * decision rather than a loss of data. What no surface shows now is spend
       * split by direction — the request table gives token counts and a total
       * cost per row, which is not the same reading.
       */
      detail: null,
    },
    { label: t("stats.requests"), value: totals.requests.toLocaleString(), detail: null, saved: 0 },
    /* A dash rather than a zero, for the same reason the activity rows use one:
       a customer whose traffic is all video has no tokens to count, and "0"
       beside a real Spent figure reads as a broken meter rather than as a
       different unit of billing. */
    {
      label: t("stats.input"),
      value: formatRequestTokens(totals.inputTokens),
      /*
       * Cache reads and writes, as a share of the input they are part of — never
       * added to it. Both are subsets of the count above: the provider reports
       * the whole prompt and details the cached part, so adding them would count
       * every hit twice.
       *
       * Shown only when non-zero, because zero is ambiguous here in a way it is
       * not elsewhere. Most providers report no cache detail at all and sum to
       * zero identically to a provider that reported a genuine miss, and a
       * permanent "0 cached" would read as caching being broken rather than
       * unreported.
       */
      detail: cacheDetail(totals, t),
      saved: 0,
    },
    {
      label: t("stats.output"),
      value: formatRequestTokens(totals.outputTokens),
      // Priced identically to the rest of the output, so this moves no charge.
      // It is the only thing that explains a reasoning model's bill: 400 output
      // tokens on a 30-token reply is a model that thought, not a broken meter.
      detail:
        totals.reasoningTokens > 0
          ? [t("stats.reasoning", { tokens: formatRequestTokens(totals.reasoningTokens) })]
          : null,
      saved: 0,
    },
  ];

  return (
    <section
      aria-label={t("stats.usage", { caption })}
      className={cx(
        "rounded-lg border border-line bg-surface transition-opacity duration-200 ease-smooth",
        stale && "opacity-50",
      )}
    >
      <p className="micro border-b border-line px-4 py-2.5">{caption}</p>
      <div className="grid grid-cols-2 divide-line sm:grid-cols-4 sm:divide-x">
        {cells.map((cell, index) => (
          <div
            key={cell.label}
            className={cx(
              "px-4 py-4",
              // The first two cells carry a divider below them on the 2×2 layout
              // the small breakpoint collapses to; on one row it would be a rule
              // across the middle of the band.
              index < 2 && "border-b border-line sm:border-b-0",
              index === 0 && "border-r border-line sm:border-r-0",
              index === 2 && "border-r border-line sm:border-r-0",
            )}
          >
            <p className="micro">{cell.label}</p>
            {/*
              Tabular mono at 1.5rem. The figures sit in a grid and are read down
              as much as across, and proportional digits make a column of numbers
              ragged at exactly the width where comparing them is the whole job.
            */}
            <p
              className={cx(
                "mt-1.5 font-mono text-2xl tracking-[-0.02em] tabular-nums",
                cell.lead ? "text-fg" : "text-fg-muted",
              )}
            >
              {cell.value}
            </p>
            {/*
              Rendered only when there is something to put in it, and now that is
              Input and Output alone — Spent lost its breakdown when both tabs
              said they did not want one.

              Three successive fixes left three comments here that contradicted
              each other, so this is the state they arrived at. The slot began as
              a fixed height held open on every cell to keep the four headline
              numbers on one baseline; then `h-4` turned out to be 16px holding
              ~16.5px of text, so the line overflowed and the saved figure below
              rendered on top of it; then it turned out the slot was not needed
              at all, because the cells are grid siblings whose content is
              top-anchored and the headlines align on their own. An empty slot
              only pushed the saving away from the figure it belongs to.

              `leading-4` survives from the middle of that: 11px text in a line
              box that fits it.
            */}
            {cell.detail && (
              <p className="mt-1 font-mono text-[11px] leading-4 tabular-nums text-fg-subtle">
                {/*
                  Each segment carries its own trailing separator and is
                  unbreakable; the plain space *between* segments is the only
                  place a line may break.

                  Both halves are needed and each was wrong on its own. Joined
                  into one string, the line broke anywhere — an orphaned middot
                  starting a line, and `per-request` split as `per-` /
                  `request`. Wrapping the separator inside the nowrap unit
                  instead removed the last whitespace between units, so the
                  browser had nowhere to break at all and the whole line
                  overflowed into the next cell. A space outside the unit
                  restores the break point; the middot inside it stays with the
                  figure it follows.
                */}
                {cell.detail.map((segment, part) => (
                  <Fragment key={segment}>
                    {part > 0 && " "}
                    <span className="whitespace-nowrap">
                      {segment}
                      {part < cell.detail!.length - 1 && " ·"}
                    </span>
                  </Fragment>
                ))}
              </p>
            )}
            {/*
              The saving sits below the detail line rather than inside it, and in
              its own colour, because it is the only figure in this band that is
              not money that was spent. A customer scanning the strip for what
              they owe must not read it as part of the total above it.
            */}
            {cell.saved > 0 && (
              <p className="mt-0.5 font-mono text-[11px] leading-4 tabular-nums text-mint">
                {t("stats.saved", { amount: formatUsageCost(cell.saved, currency) })}
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * The cache line under Input, or nothing to say.
 *
 * Reads and writes are named separately because they are opposite facts about a
 * bill. A read is a prompt the provider had already seen; a write is the call
 * that put it there, and it costs the platform about 1.25x a plain input token
 * while being billed at 1x. Summing them into one "cached" figure would merge
 * the cheap case with the expensive one.
 */
function cacheDetail(
  totals: { cachedInputTokens: number; cacheWriteTokens: number },
  t: TFunction<"ai">,
): string[] | null {
  const parts: string[] = [];
  if (totals.cachedInputTokens > 0) {
    parts.push(t("stats.cached", { tokens: formatRequestTokens(totals.cachedInputTokens) }));
  }
  if (totals.cacheWriteTokens > 0) {
    parts.push(t("stats.written", { tokens: formatRequestTokens(totals.cacheWriteTokens) }));
  }
  // Segments, like the cost split above: the strip renders the separators and
  // keeps each figure with its label rather than letting a wrap split them.
  return parts.length > 0 ? parts : null;
}
